import React, { useEffect, useRef } from 'react';

import { contactApis } from '@apis/next/contactApis';
import realContactBig from '@assets/img/icons/real-contact-big.svg';
import { Alert } from '@components/Alert';
import { Layout } from '@components/Layout';
import { ContactDisplayEditArray } from '@components/utilities/Contact/ContactDisplayEditArray';
import { Container } from '@components/utilities/Contact/Container';
import { RelatedContactArray } from '@components/utilities/Contact/RelatedContactArray';
import { VerticalSpace } from '@components/VerticalSpace';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { useSearch } from '@hooks/useSearch';
import { ContactType, contactAction } from '@models/Contact';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import * as t from 'io-ts';
import * as h from 'tyrann-io';

const strings = createStrings({
  ja: {
    title: (type: ContactType) => `${bookingInquiryStrings[type]}一覧`,
    bannerTitle: `${bookingInquiryStrings.shipper}一覧`,
    dashBoard: 'ダッシュボード',
    shipperInfo: `${bookingInquiryStrings.shipper}情報`,
    createSuccess: '正常に登録されました。',
    editSuccess: '正常に更新されました。',
  },
});

const useStyles = makeMuiStyles((_theme, common) => ({
  body: {
    ...common.flexColumn,
    flex: 1,
  },
  contactContainer: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    columnGap: 40,
    paddingBottom: 100,
  },
}));

const searchType = t.type({
  id: h.number().cast(),
  action: h.omittable(contactAction),
});

export interface ContactDetailsProps {
  type: ContactType;
}

export const ContactDetails: React.FC<ContactDetailsProps> = (props) => {
  const { type } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const scrollToBottomRef = useRef<HTMLDivElement>(null);

  const { search, updateSearch } = useSearch(searchType);
  const contact = contactApis.useGet('/contact/:id', { params: { id: search.id } });

  const successTip = () => {
    if (search.action === 'createSuccess') {
      return strings.createSuccess;
    }
    if (search.action === 'editSuccess') {
      return strings.editSuccess;
    }
    return undefined;
  };

  useEffect(() => {
    if (search.action === 'editWithScrollBottom' && !contact.isLoading) {
      requestIdleCallback(() => {
        setTimeout(() => {
          scrollToBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 1000);
      });
    }
  }, [search.action, contact.isLoading]);

  return (
    <Layout
      title={strings.title(type)}
      loading={contact.isLoading}
      errors={[contact.error]}
      breadcrumbs={[
        '/utilities/Dashboard',
        type === 'shipper'
          ? '/utilities/Contact/Shippers/Shippers'
          : '/utilities/Contact/Consignees/Consignees',
        {
          text: contact.data?.company_name ?? '-',
        },
      ]}
    >
      {() => (
        <div className={classes.body}>
          <Container>
            <VerticalSpace height={16} />

            <div className={clsx(classes.pageTitleText, classes.textPrimary)}>
              {contact.data?.company_name}
              {contact.data?.user && (
                <img
                  src={realContactBig}
                  alt="Real Contact"
                  style={{ marginLeft: '1rem', position: 'relative', top: 5 }}
                />
              )}
            </div>

            <VerticalSpace height={16} />

            <Alert
              severity="success"
              open={!!successTip()}
              onClose={() => updateSearch({ action: undefined })}
              gutter
              scrollOnOpen={false}
            >
              {successTip()}
            </Alert>

            <div className={classes.contactContainer}>
              <ContactDisplayEditArray
                contactId={search.id}
                contactType={type}
                ownedContactType={type === 'shipper' ? 'pickup_address' : 'receiver'}
              />
              <RelatedContactArray
                contactType={type === 'shipper' ? 'consignee' : 'shipper'}
                id={search.id}
              />
            </div>

            <div ref={scrollToBottomRef} />
          </Container>
        </div>
      )}
    </Layout>
  );
};
